import { useState, useId, useMemo } from "react";
import {
  LabelContext,
  TextContext,
  type GroupProps,
} from "react-aria-components";
import { type PropsWithOptionalTestID } from "@boi/components-core";

import * as S from "./PhoneInput.styled";
import { useGroupWidth } from "./PhoneInput.hooks";
import PhoneInputContext from "./PhoneInputContext";
import { Country } from "./Country";
import { Number } from "./Number";
import { InputWrapper } from "../InputWrapper";
import { InputAlerts, type InputAlertsProps } from "../InputAlerts";
import { InputLabelTopV2, type InputLabelTopV2Props } from "../InputLabelTopV2";

export type PhoneInputProps = PropsWithOptionalTestID &
  Pick<GroupProps, "aria-label" | "role" | "children"> & {
    labelProps?: Omit<InputLabelTopV2Props, "children">;
    alerts?: {
      country?: InputAlertsProps["alerts"];
      number?: InputAlertsProps["alerts"];
    };
  };

function PhoneInput(props: PhoneInputProps) {
  const { labelProps, alerts, testID = "phone-input", ...otherProps } = props;

  const hasLabel = Boolean(labelProps?.label);
  const hasLabelHint = Boolean(labelProps?.labelHint);

  const labelID = useId();
  const descriptionID = useId();

  const countryErrorMessageID = useId();
  const numberErrorMessageID = useId();

  const labelContextValue = {
    id: labelID,
    elementType: "span",
  };

  const textContextValue = {
    slots: {
      description: { id: descriptionID },
      countryErrorMessage: { id: countryErrorMessageID },
      numberErrorMessage: { id: numberErrorMessageID },
    },
  };

  const inputWrapperAlerts = useMemo(
    () => (
      <S.InputAlertsContainer>
        {alerts?.country && (
          <InputAlerts slot="countryErrorMessage" alerts={alerts.country} />
        )}

        {alerts?.number && (
          <InputAlerts slot="numberErrorMessage" alerts={alerts.number} />
        )}
      </S.InputAlertsContainer>
    ),
    [alerts]
  );

  const [groupWidth, setGroupWidth] = useState(0);
  const { groupRef } = useGroupWidth({ setGroupWidth });

  const [startSlot, setStartSlot] = useState<string | undefined>();

  return (
    <PhoneInputContext.Provider
      value={{
        countryDescribedBy: countryErrorMessageID,
        numberDescribedBy: numberErrorMessageID,
        groupWidth,
        startSlot,
        setStartSlot,
      }}
    >
      <LabelContext.Provider value={labelContextValue}>
        <TextContext.Provider value={textContextValue}>
          <InputWrapper alert={inputWrapperAlerts}>
            <InputLabelTopV2 {...labelProps}>
              <S.Group
                {...otherProps}
                ref={groupRef}
                aria-labelledby={
                  [hasLabel && labelID, hasLabelHint && descriptionID]
                    .filter(Boolean)
                    .join(" ") || undefined
                }
                data-testid={testID}
              />
            </InputLabelTopV2>
          </InputWrapper>
        </TextContext.Provider>
      </LabelContext.Provider>
    </PhoneInputContext.Provider>
  );
}

PhoneInput.Country = Country;
PhoneInput.Number = Number;

export default PhoneInput;
